// Arrows (circle or pill and a shaft with a head) and double arrows (circles at both ends),
// over the grid lines. Shafts are drawn first so the white circles cover where they start.
import type { PuzzleDoc, Rc } from '@zinc/shared';
import { centre, centresPath, ofType, rcList, styleOf } from './geom';
import { ARROW, WHITE } from './style';

/** Head at the end of a polyline: two short strokes back from the tip. */
function arrowHead(points: [number, number][]): string {
  const tip = points[points.length - 1];
  const prev = points[points.length - 2];
  if (!tip || !prev) return '';
  const dx = tip[0] - prev[0];
  const dy = tip[1] - prev[1];
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  const bx = tip[0] - ux * ARROW.head;
  const by = tip[1] - uy * ARROW.head;
  const s = ARROW.headSpread;
  return `M${bx - uy * s} ${by + ux * s}L${tip[0]} ${tip[1]}L${bx + uy * s} ${by - ux * s}`;
}

/** A circle, or a rounded capsule through the cells of a multi-cell circle (a pill). */
function Circle({ cells, color, width }: { cells: Rc[]; color: string; width: number }) {
  if (cells.length === 1) {
    const [x, y] = centre(cells[0]!);
    return (
      <circle cx={x} cy={y} r={ARROW.circle} fill={WHITE} stroke={color} strokeWidth={width} />
    );
  }
  const d = centresPath(cells);
  return (
    <g data-pill={cells.length} fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d={d} stroke={color} strokeWidth={ARROW.circle * 2 + width} />
      <path d={d} stroke={WHITE} strokeWidth={ARROW.circle * 2 - width} />
    </g>
  );
}

function nearest(from: Rc[], to: Rc): Rc {
  let best = from[0]!;
  let bestD = Infinity;
  for (const rc of from) {
    const d = (rc[0] - to[0]) ** 2 + (rc[1] - to[1]) ** 2;
    if (d < bestD) {
      best = rc;
      bestD = d;
    }
  }
  return best;
}

/** Pill cells in reading order, so the capsule follows them. */
function readingOrder(cells: Rc[]): Rc[] {
  return [...cells].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
}

export function Arrows({ doc }: { doc: PuzzleDoc }) {
  const arrows = ofType(doc, 'arrow').flatMap((e) => {
    const circle = readingOrder(rcList(e, 'circle'));
    if (circle.length === 0) return [];
    const shaft = rcList(e);
    const { color, width } = styleOf(e, ARROW);
    const points =
      shaft.length > 0 ? [centre(nearest(circle, shaft[0]!)), ...shaft.map(centre)] : [];
    return [{ circle, points, color, width }];
  });
  const doubles = ofType(doc, 'double-arrow').flatMap((e) => {
    const cells = rcList(e);
    if (cells.length === 0) return [];
    const { color, width } = styleOf(e, ARROW);
    const ends = cells.length === 1 ? [cells[0]!] : [cells[0]!, cells[cells.length - 1]!];
    return [{ cells, ends, color, width }];
  });
  return (
    <g>
      <g fill="none" strokeLinecap="round" strokeLinejoin="round">
        {arrows.map((a, i) =>
          a.points.length > 1 ? (
            <path
              key={`a${i}`}
              data-arrow-shaft=""
              d={`M${a.points.map(([x, y]) => `${x} ${y}`).join('L')}${arrowHead(a.points)}`}
              stroke={a.color}
              strokeWidth={a.width}
            />
          ) : null,
        )}
        {doubles.map((d, i) =>
          d.cells.length > 1 ? (
            <path key={`d${i}`} d={centresPath(d.cells)} stroke={d.color} strokeWidth={d.width} />
          ) : null,
        )}
      </g>
      {arrows.map((a, i) => (
        <g key={`ac${i}`} data-constraint="arrow">
          <Circle cells={a.circle} color={a.color} width={a.width} />
        </g>
      ))}
      {doubles.map((d, i) => (
        <g key={`dc${i}`} data-constraint="double-arrow">
          {d.ends.map((rc, k) => (
            <Circle key={k} cells={[rc]} color={d.color} width={d.width} />
          ))}
        </g>
      ))}
    </g>
  );
}
